import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { Alert } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import type { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import FilterContainer from '@src/components/FilterContainer';
import ListViewportLayout from '@src/components/ListViewportLayout';
import SecondaryPanel from '@src/components/SecondaryPanel/SecondaryPanel';
import { ResponsiveTable } from '@src/components/Table';
import {
  SHIFT_LIST_PAGE_SIZE,
  SHIFT_LIST_VIEW_STORAGE_KEY,
} from '@src/packages/shifts/constants';
import ShiftFilters from '@src/packages/shifts/components/ShiftFilters';
import { ALLOWED_ROLES, PaginationWithFirestore } from '@src/packages/utils';
import IntlMessages from '@src/util/IntlMessages';

import { useShiftsColumns } from './hooks/useShiftsColumns';
import { useShiftsList } from './hooks/useShiftsList';

function Shifts() {
  const intl = useIntl();
  const {
    filters,
    initialFilters,
    setFilters,
    toggle,
    isOpen,
    paginatedData,
    paginationState,
    loading,
    error,
    searchTruncation,
    refetchSearchIds,
  } = useShiftsList();

  const { columns, handleAddShift } = useShiftsColumns();

  const filterPanel = useMemo(
    () => <ShiftFilters handleFilters={setFilters} filters={filters} loading={loading} />,
    [filters, setFilters, loading],
  );

  const filterToggleLabel = intl.formatMessage({
    id: isOpen ? 'filterDropdown.closeFilters' : 'dashboard.tooltip.filter',
  });

  return (
    <>
      <SecondaryPanel.Panel
        title={intl.formatMessage({ id: 'dashboards.chart.filters' })}
        initialValues={initialFilters as FilterValues}
      >
        {filterPanel}
      </SecondaryPanel.Panel>
      <BoxContainer fullContent fixed bottomBorder>
        <FilterContainer
          title={<Title.Header value={<IntlMessages id="shifts.title" />} />}
          actionButtons={[
            {
              label: filterToggleLabel,
              iconName: 'filter_alt',
              type: 'secondary',
              action: toggle,
              className: isOpen ? 'gx-border-primary' : '',
            },
            {
              label: <IntlMessages id="button.create" />,
              iconName: 'add',
              type: 'primary',
              action: handleAddShift,
              allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.CREATE,
            },
          ]}
        />
      </BoxContainer>
      <FilterActiveTags.Container>
        <FilterActiveTags.Bar>
          <FilterActiveTags.ScrollArea>
            <FilterActiveTags onAddFilter={toggle} />
          </FilterActiveTags.ScrollArea>
        </FilterActiveTags.Bar>
      </FilterActiveTags.Container>
      <BoxContainer fullContent error={error ?? undefined} onErrorCallback={refetchSearchIds}>
        <SecondaryPanel.Content>
          {searchTruncation && (
            <Alert
              className="gx-mb-3"
              type="info"
              showIcon
              message={intl.formatMessage(
                { id: 'shifts.search.truncated' },
                { shown: searchTruncation.shown, total: searchTruncation.total },
              )}
            />
          )}
          <ListViewportLayout
            viewportOffset={160}
            sidePanelOpen={isOpen}
            footer={
              <PaginationWithFirestore
                paginationState={paginationState}
                options={{
                  showSizeChanger: true,
                  pageSizeOptions: [String(SHIFT_LIST_PAGE_SIZE), '20', '50', '100'],
                }}
              />
            }
          >
            <ResponsiveTable
              type="light"
              dataSource={paginatedData}
              columns={columns}
              loading={loading}
              className="gx-table-responsive"
              sortStorageKey={SHIFT_LIST_VIEW_STORAGE_KEY}
              {...{ rowKey: 'id' }}
            />
          </ListViewportLayout>
        </SecondaryPanel.Content>
      </BoxContainer>
    </>
  );
}

export default Shifts;
